import React from "react";
import { useSelector } from "react-redux";

import SntOrgIcon from "@icons/SntOrgIcon";
import SntStockOrgIcon from "@icons/SntStockOrgIcon";
import OrganizationType from "@/cleanup/utils/OrganizationType";
import { useOrg } from "@/contexts/OrgContext";
import { translate } from "@/hooks/useLanguage";
import { isMultiOrgLikeVariant } from "@/utils/partnerVariant";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { RootState } from "@/types/RootState";

/**
 * Minimal shape of an owning organization as inlined in entity payloads
 * (device.ownedByOrg, area.ownedByOrg, alertRule.ownedByOrg, user.organisation).
 */
export interface OwnerOrg {
  id: number;
  name?: string;
  organisationType?: string;
  partner?: {
    name?: string | null;
    partnerVariant?: string | null;
  } | null;
}

/**
 * Flat owner fields as projected by the advanced-search configs
 * (`organisationId` / `organisationName` / `organisationType` /
 * `organisationPartnerName` / `organisationPartnerVariant` aliases in
 * columnDefinition.json). Two repositories spell the id and name differently
 * and are read here too, so that one mapper serves every list (STIC-15806):
 * the images, geolayer and site-map repositories and the agent DTOs alias the
 * name `ownedByOrgName`; the usage-profile repository aliases the owner
 * `ownedId` / `ownedName`, and its query emits `ownedId` as a default
 * selection, so an `organisationId` column there collides with itself
 * ("Multi-select expressions have duplicate alias").
 */
export interface OwnerRowFields {
  organisationId?: number | string | null;
  ownedId?: number | string | null;
  organisationName?: string | null;
  ownedByOrgName?: string | null;
  ownedName?: string | null;
  organisationType?: string | null;
  organisationPartnerName?: string | null;
  organisationPartnerVariant?: string | null;
}

/**
 * The backend's owner reference, `OrganisationReferenceDto`: the partner is
 * flattened to `partnerName` / `partnerVariant`. It travels as `owner` on
 * dashboards, `ownedBy` on usage profiles and `ownedByOrg` on processes,
 * report schedules and CSM comments.
 */
export interface OwnerReference {
  id?: number | null;
  name?: string | null;
  organisationType?: string | null;
  partnerName?: string | null;
  partnerVariant?: string | null;
}

/**
 * `organisationType` as the resource happened to serialize it. Most resources
 * send the bare enum name; GeoLayerResourceImpl and SiteMapResourceImpl
 * register GenericEnumAdapterFactory, which writes every enum as
 * `{ "name": "PARTNER" }` — so the level badge and the Partner display name,
 * which both test the type against a string, silently never matched on those
 * two pages (STIC-15806).
 */
export const organisationTypeOf = (value: unknown): string | undefined => {
  if (typeof value === "string") return value;
  const name = (value as { name?: unknown } | null)?.name;
  return typeof name === "string" ? name : undefined;
};

export const ownerFromRow = (row: OwnerRowFields | null | undefined): OwnerOrg | null => {
  const id = Number(row?.organisationId ?? row?.ownedId);
  if (!row || !Number.isFinite(id) || id <= 0) return null;
  return {
    id,
    name: row.organisationName ?? row.ownedByOrgName ?? row.ownedName ?? undefined,
    organisationType: organisationTypeOf(row.organisationType),
    partner:
      row.organisationPartnerName || row.organisationPartnerVariant
        ? { name: row.organisationPartnerName, partnerVariant: row.organisationPartnerVariant }
        : null,
  };
};

/** The owner as the detail header reads it, from an `OrganisationReferenceDto`. */
export const ownerFromReference = (ref: OwnerReference | null | undefined): OwnerOrg | null => {
  if (!ref?.id) return null;
  return {
    id: ref.id,
    name: ref.name ?? undefined,
    organisationType: organisationTypeOf(ref.organisationType),
    partner:
      ref.partnerName || ref.partnerVariant
        ? { name: ref.partnerName, partnerVariant: ref.partnerVariant }
        : null,
  };
};

/**
 * STIC-15806 visibility rule: ownership is only worth showing when it differs
 * from the organization the user is currently working in.
 * - "All" selected: more than one organization is in view, so always show.
 * - A single organization in scope (own level, or `?org=` sliced from the cross
 *   level): show only for entities owned elsewhere — the ones inherited from a
 *   partner/system level above, which the user cannot edit.
 *
 * `scopeOrgId` already answers "which single organization is in scope", and it
 * resolves own level to the login's own org, so this needs no separate NORMAL
 * branch (it used to read the old `orgId`, which conflated own level with All —
 * the STIC-15790 bug class that check-org-scope now rejects).
 */
export const useShowOwnership = (): ((owner: OwnerOrg | null | undefined) => boolean) => {
  const { scopeOrgId, isAllOrgs } = useOrg();
  return (owner) => !!owner && (isAllOrgs || owner.id !== scopeOrgId);
};

/**
 * Header rule: a detail page is about one object, so its ownership line is
 * always worth showing to partner/system users (who work across organizations),
 * and to an organization user only when the object is not their own (inherited
 * from partner/system level). Unlike the list rule this ignores the selector:
 * several detail pages set ?org= to the owner on load, which would otherwise
 * hide the line exactly where it is most useful.
 */
export const useShowHeaderOwnership = (): ((owner: OwnerOrg | null | undefined) => boolean) => {
  const viewerOrg = useSelector((state: RootState) => state.user?.organisation);
  return (owner) =>
    !!owner && (viewerOrg?.organisationType !== "NORMAL" || owner.id !== viewerOrg?.id);
};

/**
 * Hierarchy level of the owner as a word: "System", "Partner", "Multi-org".
 * A plain organization has no level word — it is the common case and needs no marker.
 */
export const getOwnerLevelLabel = (ownedByOrg: OwnerOrg): string | null => {
  if (ownedByOrg.organisationType === "SYSTEM") {
    return translate(LanguageKey.system_key);
  }
  if (ownedByOrg.organisationType === "PARTNER") {
    return isMultiOrgLikeVariant(ownedByOrg.partner?.partnerVariant)
      ? translate(LanguageKey.multi_org_key)
      : translate(LanguageKey.partner_key);
  }
  return null;
};

/**
 * Display name of an owner:
 * - SYSTEM: the word "System" — never the system organisation's real name
 *   (e.g. "Sensolus"), so the platform stays white-label friendly.
 * - PARTNER: the Partner's name (admin.partner.name), not the partner-type
 *   organisation's name, which by convention carries a "<partner> partner
 *   account" suffix that is too wide and distracting on a second line.
 * - otherwise: the organisation name.
 */
export const getOwnerDisplayName = (ownedByOrg: OwnerOrg): string | undefined => {
  if (ownedByOrg.organisationType === "SYSTEM") {
    return translate(LanguageKey.system_key);
  }
  if (ownedByOrg.organisationType === "PARTNER" && ownedByOrg.partner?.name) {
    return ownedByOrg.partner.name;
  }
  return ownedByOrg.name;
};

/**
 * One-letter hierarchy level for the badge: S system, P channel partner, M
 * multi-org-like partner (STIC-16004: every variant but CHANNEL_PARTNER, the
 * same test the menus and partner forms use) — and none for a plain
 * organization, which is the common case and
 * needs no marker (same rule as getOwnerLevelLabel). An "O" next to the
 * organization name said nothing the name did not already say, and in the
 * header it repeated the organization glyph beside it.
 */
export const getOwnerLevelLetter = (ownedByOrg: OwnerOrg): "S" | "P" | "M" | null => {
  if (ownedByOrg.organisationType === "SYSTEM") return "S";
  if (ownedByOrg.organisationType === "PARTNER") {
    return isMultiOrgLikeVariant(ownedByOrg.partner?.partnerVariant) ? "M" : "P";
  }
  return null;
};

interface EntityOwnershipProps {
  ownedByOrg?: OwnerOrg | null;
  /** List cells: same unit without the organization glyph (the column header carries the meaning). */
  compact?: boolean;
  className?: string;
}

/**
 * Standard ownership line (STIC-15806, "option 7"). One unit for the detail
 * header and for the second row of a list's primary-identifier cell:
 *
 *   header:  ⌂ [P] partner_real EU     ⌂ org_real     ⌂ [S] System
 *   list:      [P] partner_real EU       org_real       [S] System
 *
 * - A very muted one-letter level badge (S system / P partner / M multi-org
 *   partner) in front of the owner, for owners that sit above a plain
 *   organization; an ordinary org gets no badge. The tooltip spells it out.
 *   Only partner/system viewers get it (STIC-16164): an organization user sees
 *   the owner's name alone.
 * - The header adds the organization glyph (tooltip "Owned by") so the line
 *   explains itself without repeating the words; lists drop it — the column
 *   header carries the meaning once.
 * - Small text, so it reads as a subtitle to the name; the owner is plain
 *   text, never a hyperlink.
 * Visibility (`useShowOwnership`) is decided by the caller.
 */
const EntityOwnership: React.FC<EntityOwnershipProps> = ({
  ownedByOrg,
  compact = false,
  className = "",
}) => {
  const viewerOrgType = useSelector((state: RootState) => state.user?.organisation?.organisationType);
  if (!ownedByOrg) return null;

  const displayName = getOwnerDisplayName(ownedByOrg);
  // The level badge is for partner/system users, who work across the hierarchy.
  // An organization user only needs to know who owns an inherited object.
  const levelLetter = viewerOrgType === "NORMAL" ? null : getOwnerLevelLetter(ownedByOrg);
  const levelLabel = getOwnerLevelLabel(ownedByOrg) ?? translate(LanguageKey.organization_key);

  // Plain inline flow (not flex): in a narrow column the name wraps at word
  // boundaries while the glyph and badge stay glued to its first word.
  // .badge-owner-level (custom-elements.scss) pins the ~9px size and the faint look.
  return (
    <span className={`text-muted small ${className}`} title={translate(LanguageKey.owned_by_key)}>
      {!compact &&
        (ownedByOrg.organisationType === OrganizationType.STOCK ? (
          <SntStockOrgIcon size={12} className="align-middle me-1" />
        ) : (
          <SntOrgIcon size={12} className="align-middle me-1" />
        ))}
      {levelLetter && (
        <span
          className="badge badge-soft-sensolus-grey badge-owner-level align-middle me-1"
          title={levelLabel}
        >
          {levelLetter}
        </span>
      )}
      {displayName}
    </span>
  );
};

export default EntityOwnership;
